// Code generated by tygo. DO NOT EDIT.

//////////
// source: activation_constants.go

/**
 * Activation journey constants - exported to shared types for consistent use
 * across frontend and backend activation policy surfaces.
 */
export const MaxActivationStep = 11;
/**
 * Activation journey constants - exported to shared types for consistent use
 * across frontend and backend activation policy surfaces.
 */
export const ActivationLearningWordsGoal = 22;
/**
 * Activation journey constants - exported to shared types for consistent use
 * across frontend and backend activation policy surfaces.
 */
export const ActivationReviewedCardsGoal = 22;
/**
 * Activation journey constants - exported to shared types for consistent use
 * across frontend and backend activation policy surfaces.
 */
export const ActivationKnownWordsGoal = 21;
/**
 * Activation journey constants - exported to shared types for consistent use
 * across frontend and backend activation policy surfaces.
 */
export const ActivationCompletedLessonsGoal = 8;
/**
 * Activation journey constants - exported to shared types for consistent use
 * across frontend and backend activation policy surfaces.
 */
export const ActivationPhrasesGoal = 11;
/**
 * Activation journey constants - exported to shared types for consistent use
 * across frontend and backend activation policy surfaces.
 */
export const ActivationListening15MinGoal = 900;
/**
 * Activation journey constants - exported to shared types for consistent use
 * across frontend and backend activation policy surfaces.
 */
export const ActivationListening30MinGoal = 1800;
/**
 * Activation journey constants - exported to shared types for consistent use
 * across frontend and backend activation policy surfaces.
 */
export const ActivationListening45MinGoal = 2700;
/**
 * Activation journey constants - exported to shared types for consistent use
 * across frontend and backend activation policy surfaces.
 */
export const ActivationListening60MinGoal = 3600;

//////////
// source: activity_dto.go

export interface GenerateDistractorsRequest {
  wordKey: string; // Pre-normalized Spanish word (used for cache lookup AND LLM prompt)
  correctNativeWord: string; // English word (for LLM prompt)
  availableWords: string[];
  videoLevel: number /* float64 */; // Numeric level (e.g., 1.28) - converted to CEFR in handler
  distractorLanguage: string; // "spanish" or "english"
}
export interface GenerateDistractorsResponse {
  distractors: string[];
}

//////////
// source: agent_dto.go

/**
 * Agent user search result
 */
export interface AgentUserSearchResult {
  userId: number /* int64 */;
  name: string;
  email: string;
  role: string;
  createdAt: string;
}
export interface AgentUserSearchResponse {
  users: AgentUserSearchResult[];
  totalCount: number /* int */;
}
/**
 * AgentCreateUserRequest is the body for creating a new account via an agent token.
 * Role is optional and defaults to "free"; the privileged "admin" role cannot be
 * created through this endpoint.
 */
export interface AgentCreateUserRequest {
  email: string;
  password: string;
  displayName: string;
  role: string;
}
/**
 * AgentCreateUserResponse is returned after an agent creates a new account.
 */
export interface AgentCreateUserResponse {
  userId: number /* int64 */;
  email: string;
  role: string;
  message: string;
}
/**
 * Agent user detail
 */
export interface AgentUserDetailResponse {
  userId: number /* int64 */;
  name: string;
  email: string;
  role: string;
  createdAt: string;
  stripeCustomerId?: string;
  revenueCatId?: string;
  trialEndAt?: string;
  placementResult?: string;
  parrotLevel: number /* int */;
  /**
   * Subscription info
   */
  subscriptionStatus: string;
  subscriptionProduct?: string;
  subscriptionStore?: string;
  subscriptionExpiresAt?: string;
  /**
   * Stats
   */
  knownWords: number /* int */;
  learningWords: number /* int */;
  listenTime: number /* int */;
  chaptersCompleted: number /* int */;
  streak: number /* int */;
}
/**
 * Agent set partner response
 */
export interface AgentSetPartnerResponse {
  message: string;
  userId: number /* int64 */;
  role: string;
}
/**
 * AgentUnlockFeedResponse is returned after unlocking the in-app feed for a user.
 */
export interface AgentUnlockFeedResponse {
  message: string;
  userId: number /* int64 */;
  activationStep: number /* int */;
  highestEarnedLevel: number /* int */;
}
/**
 * AgentSetStreakRequest is the body for setting a user's current streak.
 */
export interface AgentSetStreakRequest {
  streak: number /* int */;
}
/**
 * AgentSetStreakResponse is returned after setting a user's streak.
 */
export interface AgentSetStreakResponse {
  message: string;
  userId: number /* int64 */;
  streak: number /* int */;
}
/**
 * AgentSetNameRequest is the body for setting a user's display name.
 */
export interface AgentSetNameRequest {
  name: string;
}
/**
 * AgentSetNameResponse is returned after setting a user's display name. It
 * reports the stored display name plus the first/last split derived from it.
 */
export interface AgentSetNameResponse {
  message: string;
  userId: number /* int64 */;
  displayName: string;
  firstName: string;
  lastName: string;
}
/**
 * API Token admin DTOs
 */
export interface CreateApiTokenRequest {
  name: string;
}
export interface CreateApiTokenResponse {
  token: string;
  name: string;
  id: number /* int64 */;
}
export interface ApiTokenListItem {
  id: number /* int64 */;
  name: string;
  createdAt: string;
  lastUsedAt?: string;
  createdBy: string;
}
export interface ListApiTokensResponse {
  tokens: ApiTokenListItem[];
}
/**
 * Agent feedback DTOs
 */
export interface AgentFeedbackSource {
  id: number /* int64 */;
  userName: string;
  userEmail: string;
  sourceType: string;
  sourceDate: string;
  tags: string[];
  itemCount: number /* int */;
}
export interface AgentFeedbackSearchResponse {
  sources: AgentFeedbackSource[];
  totalCount: number /* int */;
}
export interface AgentFeedbackStatsResponse {
  totalItems: number /* int */;
  totalSources: number /* int */;
  categoryCounts: { [key: string]: number /* int */};
}
export interface AgentFeedbackItem {
  id: number /* int64 */;
  category: string;
  feedbackText: string;
  tag: string;
  contextSnippet?: string;
}
export interface AgentFeedbackSourceDetailResponse {
  id: number /* int64 */;
  userName: string;
  userEmail: string;
  sourceType: string;
  sourceDate: string;
  rawContent: string;
  summary?: string;
  tags: string[];
  items: AgentFeedbackItem[];
}
/**
 * Agent NPS DTOs
 */
export interface AgentNPSResponse {
  id: number /* int64 */;
  userId: number /* int64 */;
  displayName: string;
  email: string;
  score: number /* int */;
  feedback: string;
  createdAt: string;
  isContacted: boolean;
  isSkipScore: boolean;
  placementResult?: string;
}
export interface AgentNPSListResponse {
  responses: AgentNPSResponse[];
}
export interface AgentNPSContactedRequest {
  isContacted: boolean;
}
/**
 * Agent deletion log DTOs
 */
export interface AgentDeletionLogEntry {
  id: number /* int64 */;
  userId: number /* int64 */;
  email: string;
  displayName: string;
  role: string;
  deletedAt: string;
  reason: string;
}
export interface AgentDeletionLogResponse {
  entries: AgentDeletionLogEntry[];
  totalCount: number /* int */;
}

//////////
// source: analytics_dto.go

export interface CohortRowDTO {
  cohortLabel: string;
  cohortSize: number /* int */;
  retention: number /* float64 */[];
  isComplete: boolean[];
}
export interface LevelChurnDataDTO {
  level: string;
  churnedCount: number /* int */;
  churnPercentage: number /* float64 */;
}
export interface LevelSignupDataDTO {
  level: string;
  signupCount: number /* int */;
  signupPercentage: number /* float64 */;
}
export interface SubscriptionCohortRetentionResultDTO {
  cohorts: SubscriptionCohortRowDTO[];
}
export interface SubscriptionCohortRowDTO {
  cohortLabel: string;
  cohortStart: any /* time.Time */;
  cohortSize: number /* int */;
  periods: number /* float64 */[];
  absoluteCounts: number /* int */[];
}
export interface FrequencyPeriodDTO {
  label: string;
  totalUsers: number /* int */;
  activeUsers: number /* int */;
  avgActiveDays: number /* float64 */;
  activeRate: number /* float64 */;
}
/**
 * ChurnByLevelResponse is the response for churn by level endpoint
 */
export interface ChurnByLevelResponse {
  month: string;
  levelSource: string;
  totalChurned: number /* int */;
  totalQueried: number /* int */;
  data: LevelChurnDataDTO[];
}
/**
 * SignupsByLevelResponse is the response for signups by level endpoint
 */
export interface SignupsByLevelResponse {
  month: string;
  levelSource: string;
  totalSignups: number /* int */;
  data: LevelSignupDataDTO[];
}
/**
 * CohortRetentionResponse is the response for cohort retention endpoints
 */
export interface CohortRetentionResponse {
  cohorts: CohortRowDTO[];
  total: number /* int */;
  cachedAt: string;
}
/**
 * MatchSubscriptionsResponse is the response for matching subscriptions to users
 */
export interface MatchSubscriptionsResponse {
  matchedCount: number /* int */;
  message: string;
}
/**
 * UsageFrequencyResponse is the response for usage frequency endpoint
 */
export interface UsageFrequencyResponse {
  periods: FrequencyPeriodDTO[];
  cachedAt: string;
}
/**
 * ImportSubscriptionsResponse is the response for CSV import endpoint
 */
export interface ImportSubscriptionsResponse {
  totalRows: number /* int */;
  importedRows: number /* int */;
  skippedRows: number /* int */;
  matchedUsers: number /* int */;
  churnedUsers: number /* int */;
  activeUsers: number /* int */;
  errors: number /* int */;
  duration: string;
  message: string;
}

//////////
// source: app_update_dto.go

/**
 * UpdateType is exported for TypeScript type generation
 * tygo:name UpdateType
 */
export type UpdateType = string;
export const UpdateTypeNone: UpdateType = "none";
export const UpdateTypeSoft: UpdateType = "soft";
export const UpdateTypeHard: UpdateType = "hard";
/**
 * UpdateCheckResponse is the response for GET /update-check
 */
export interface UpdateCheckResponse {
  updateType: UpdateType;
  storeUrl?: string;
  latestVersion?: string;
}
/**
 * GetUpdateConfigResponse is the response for GET /edit/update-config
 */
export interface GetUpdateConfigResponse {
  id: number /* int */;
  platform: string;
  min_supported_version?: string;
  recommended_version?: string;
  store_url_ios: string;
  enabled: boolean;
}
/**
 * PutUpdateConfigRequest is the request for PUT /edit/update-config
 */
export interface PutUpdateConfigRequest {
  platform: string;
  minSupportedVersion?: string;
  recommendedVersion?: string;
  storeUrlIos: string;
  enabled: boolean;
}
/**
 * VersionStat represents version distribution statistics
 */
export interface VersionStat {
  appVersion: string;
  platform: string;
  userCount: number /* int */;
  lastSeen: string;
}
/**
 * GetVersionStatsResponse is the response for GET /edit/version-stats
 */
export interface GetVersionStatsResponse {
  stats: VersionStat[];
}

//////////
// source: audio_dto.go

/**
 * GetWordAudioResponse is the response for getting a word's audio URL
 */
export interface GetWordAudioResponse {
  url: string;
  word: string;
  accent: string;
  gender: string;
}
/**
 * PrefetchAudioRequest is the request for batch prefetching audio files
 */
export interface PrefetchAudioRequest {
  words: string[]; // List of words to prefetch
  accent?: string; // Optional accent override (uses user preference if not set)
  gender?: string; // Optional gender override (uses user preference if not set)
}
/**
 * PrefetchAudioResponse is the response for batch prefetching
 */
export interface PrefetchAudioResponse {
  /**
   * URLs maps word -> public URL (always returned, even if file needs generation)
   */
  urls: { [key: string]: string};
  /**
   * Accent used for this batch
   */
  accent: string;
  /**
   * Gender used for this batch
   */
  gender: string;
  /**
   * BaseURL is the pattern for constructing URLs client-side
   * Format: "https://bucket.endpoint/audio/words/{accent}/{gender}/{word}.mp3"
   */
  baseUrl: string;
}
/**
 * AccentOption represents a single accent option for the client
 */
export interface AccentOption {
  id: string;
  displayName: string;
}
/**
 * GenderOption represents a single gender option for the client
 */
export interface GenderOption {
  id: string;
  displayName: string;
}
/**
 * GetAvailableVoiceOptionsResponse is the response for listing available voice options
 */
export interface GetAvailableVoiceOptionsResponse {
  accents: AccentOption[];
  defaultAccent: string;
  selectedAccent?: string; // User's current accent selection
  genders: GenderOption[];
  defaultGender: string;
  selectedGender?: string; // User's current gender selection
  /**
   * BaseURL pattern for constructing audio URLs client-side
   * Replace {accent}, {gender}, and {word} with actual values
   */
  baseUrl: string;
}
/**
 * GetAvailableAccentsResponse is deprecated, use GetAvailableVoiceOptionsResponse
 * Kept for backward compatibility
 */
export type GetAvailableAccentsResponse = GetAvailableVoiceOptionsResponse;

//////////
// source: card_dto.go

export interface GetCardsResponse {
  cards: Card[];
}
/**
 * ReviewCardType distinguishes between word cards and phrase cards
 * tygo:name ReviewCardType
 */
export type ReviewCardType = string;
export const ReviewCardTypeWord: ReviewCardType = "word";
export const ReviewCardTypePhrase: ReviewCardType = "phrase";
export interface Card {
  id: number /* int64 */;
  userId: number /* int64 */;
  cardType: ReviewCardType; // "word" or "phrase"
  reviewWord: UserVideoWord;
  contextText: UserVideoWord[];
  dueAt: string;
  youtubeId: string;
  aspectRatio: AspectRatio;
  videoEndMs: number;
  player: 'unset' | 'cloudflare' | 'youtube';
  cloudflareId: string;
  nextDueIfGood: string; // ISO timestamp - projected due if Good
  nextDueIfAgain: string; // ISO timestamp - projected due if Again
  /**
   * Phrase-specific fields (only set when CardType == "phrase")
   */
  phraseText?: string; // The phrase in target language (Spanish)
  phraseTranslation?: string; // The translation in native language (English)
}
export interface GetCardCountResponse {
  count: number /* int */;
}
export type CardAction = 'good' | 'again';
export interface PutCardRequest {
  cardId: number /* int64 */;
  action: CardAction;
}
export interface PutCardRequestResponse {
  message: string;
  waterDropletsAwarded: number /* int */;
}
export interface DeleteCardRequest {
  cardId: number /* int64 */;
}

//////////
// source: chapter_feed_analytics_dto.go

/**
 * DTOs
 */
export interface PostChapterViewedRequest {
  chapterId: number /* int64 */;
}
export interface PostChapterViewedResponse {
  message: string;
}

//////////
// source: chapter_feed_dto.go

/**
 * DTOs
 */
export interface GetFeedResponse {
  chapters: ChapterFeed[];
}
export interface ChapterFeed {
  chapterID: number /* int64 */;
  lessonIDs: number /* int64 */[];
  dialect: 'latam' | 'spain' | 'unknown';
  title: string;
}
/**
 * V2 Response with IsLiked field.
 * Looping=true means the server has run out of FRESH chapters for this user
 * and has switched to recycling already-seen content. "Fresh" is a chapter
 * NOT excluded by either:
 *   - user_feed_chapter (lifetime — set by POST /chapter/viewed), or
 *   - user_recently_shown_chapter within the service's cacheExpiry
 *     window (rolling TTL — set by GetFeedV2 itself on every served page).
 * Contract:
 *   - While fresh content remains, looping=false and every reachable chapter
 *     is served exactly once, no repeats.
 *   - Once fresh content is exhausted, looping flips to true AND the page is
 *     topped up to a full 5 from already-seen content (least-recently-shown
 *     first). From then on the feed is an infinite recycle — it never returns
 *     an empty page. Clients do NOT need to act on looping; it's an honest
 *     "what follows includes repeats" hint they may surface if they wish.
 * Because exhaustion is measured against the cacheExpiry TTL, looping is a
 * "right now" signal: after cacheExpiry elapses since a chapter was last
 * served-without-being-viewed it becomes fresh again, so a later call can
 * return looping=false. `parrot feed verify` and the
 * TestGetFeedV2_ServesAllThenLoops CI gate drain in a tight loop well inside
 * that window, so they observe the clean "all unique once, then recycle"
 * contract.
 */
export interface GetFeedV2Response {
  chapters: ChapterFeedV2[];
  looping: boolean;
}
export interface ChapterFeedV2 {
  chapterID: number /* int64 */;
  lessonIDs: number /* int64 */[];
  dialect: 'latam' | 'spain' | 'unknown';
  title: string;
  isLiked: boolean;
}
/**
 * FeedTag represents a topic tag available for feed personalization
 */
export interface FeedTag {
  name: string; // Internal tag name (e.g., "travel", "food")
  displayName: string; // Human-readable name for UI display
}
/**
 * GetFeedTagsResponse returns available topic tags for feed personalization
 */
export interface GetFeedTagsResponse {
  tags: FeedTag[];
}

//////////
// source: conversation_dto.go

/**
 * SendMessageRequest is the body for sending a message
 */
export interface SendMessageRequest {
  body: string;
  client_version?: string;
}
/**
 * ConversationMessageResponse represents a single message
 */
export interface ConversationMessageResponse {
  id: number /* int64 */;
  conversation_id: number /* int64 */;
  sender_type: string;
  sender_name: string;
  sender_avatar_url?: string;
  sender_bio?: string;
  sender_title?: string;
  body: string;
  created_at: string;
}
/**
 * ConversationResponse represents a conversation with its messages
 */
export interface ConversationResponse {
  id: number /* int64 */;
  status: string;
  messages: ConversationMessageResponse[];
  created_at: string;
}
/**
 * ConversationSummaryResponse represents a conversation summary for admin list
 */
export interface ConversationSummaryResponse {
  id: number /* int64 */;
  user_id: number /* int64 */;
  user_name: string;
  user_email: string;
  status: string;
  last_message_preview: string;
  last_message_at: string;
  message_count: number /* int */;
  created_at: string;
}
/**
 * ConversationListResponse is the paginated list of conversations
 */
export interface ConversationListResponse {
  conversations: ConversationSummaryResponse[];
  total: number /* int */;
}

//////////
// source: dev_dto.go

/**
 * SeedUserStateLessonProgress is a single entry in
 * SeedUserStateRequest.LessonsProgress.
 */
export interface SeedUserStateLessonProgress {
  lessonId: number /* int64 */;
  percent: number /* int */;
}
/**
 * SeedUserStateCards mirrors the SRS state buckets that dev seed cards support.
 * Each int is a count; zero/omitted means skip that bucket.
 */
export interface SeedUserStateCards {
  new?: number /* int */;
  learning?: number /* int */;
  mature?: number /* int */;
  due?: number /* int */;
}
/**
 * SeedUserStateRequest is the body of POST /dev/seed/user-state.
 */
export interface SeedUserStateRequest {
  role: UserRole;
  email?: string;
  streakDays?: number /* int */;
  plan?: string; // free|trial|premium; omit to leave billing state untouched
  planExpiresAt?: string; // RFC3339 timestamp
  waterDroplets?: number /* int */;
  lessonsProgress?: SeedUserStateLessonProgress[];
  cards?: SeedUserStateCards;
  cardsLessonId?: number /* int64 */; // optional, attached to all seeded cards
}
/**
 * SeedUserStateResponse is the body returned from POST /dev/seed/user-state.
 */
export interface SeedUserStateResponse {
  userId: number /* int64 */;
  token: string;
  email: string;
  role: UserRole;
}
/**
 * SeedProfileCatalogItem is one local-dev preload profile returned by
 * GET /dev/seed/profiles. Keys are stable and can be POSTed to
 * /dev/seed/profile.
 */
export interface SeedProfileCatalogItem {
  key: string;
  emoji: string;
  label: string;
  description: string;
  stats: string[];
  redirect: string;
}
/**
 * SeedProfileRequest is the body of POST /dev/seed/profile.
 */
export interface SeedProfileRequest {
  key: string;
  email?: string;
}
/**
 * SeedProfileResponse is returned by POST /dev/seed/profile.
 */
export interface SeedProfileResponse {
  userId: number /* int64 */;
  token: string;
  email: string;
  role: UserRole;
  profileKey: string;
  redirect: string;
}
/**
 * UsersSearchResult is one row in the response of GET /dev/users/search.
 */
export interface UsersSearchResult {
  userId: number /* int64 */;
  email: string;
  role: UserRole;
  streakDays: number /* int */;
  matureCards: number /* int */;
  hasMatureCards: boolean;
  completedLessons: number /* int */;
  waterDroplets: number /* int */;
  createdAt: string;
}
/**
 * PersonaCatalogItem is one archetype entry returned by GET /dev/personas.
 * Keys are stable — clients POST them to /dev/personas/:key/login.
 */
export interface PersonaCatalogItem {
  key: string;
  emoji: string;
  label: string;
  description: string;
  stats: string[];
}
/**
 * PersonaLoginResponse is returned by POST /dev/personas/:key/login.
 * Email and display name are deliberately absent — no PII leaves this endpoint.
 */
export interface PersonaLoginResponse {
  token: string;
  role: UserRole;
}

//////////
// source: dictionary_dto.go

/**
 * DTOs
 */
export interface GetDictionaryStatsResponse {
  knownWordCount: number /* int */;
  learningWordCount: number /* int */;
  knownPhraseCount: number /* int */;
  learningPhraseCount: number /* int */;
}
export interface UserWord {
  userWordId: number /* int64 */;
  targetWord: string;
  nativeWord: string;
  status: 'new' | 'learning' | 'known' | 'deleted';
  wordKey: string;
  translationId: number /* int64 */;
}
export interface GetKnownWordsDictionaryResponse {
  knownWords: UserWord[];
  nextPage: number /* int */;
  totalCount: number /* int */;
}
export interface GetSearchWordsResponse {
  words: UserWord[];
  nextPage: number /* int */;
  totalCount: number /* int */;
}
export interface GetLearningWordsDictionaryResponse {
  learningWords: UserWord[];
  nextPage: number /* int */;
  totalCount: number /* int */;
}
export interface PutWordStatusResponse {
  message: string;
  waterDropletsAwarded: number /* int */;
}
export interface GetPhraseRequest {
  startVideoWordId: number /* int64 */;
  endVideoWordId: number /* int64 */;
  /**
   * The name testing string was chosen since we thought we
   * would remove it, but we are keeping it and now it can't
   * change since client versions depend on it. If we change it
   * it must be a backwards compatible change.
   */
  testingString: string;
  /**
   * Optional: Only sent from feed view to track lesson interactions
   */
  trackLessonInteraction?: LessonInteractionInfo;
  lessonId?: number /* int64 */; // Only needed when tracking lesson interaction
}
/**
 * LessonInteractionInfo provides information for tracking lesson interactions
 * Only included when calling from the feed view
 */
export interface LessonInteractionInfo {
  chapterId: number /* int64 */;
  lessonNumber: number /* int */;
  userLevel: string;
}
export interface GetPhraseResponse {
  phraseId: number /* int64 */;
  /**
   * this must stay as "phrase". for backwards compatibility.
   */
  phrase: string;
  isPhraseSaved: boolean;
}
export interface PutPhraseSavedRequest {
  phraseId: number /* int64 */;
  lessonId: number /* int64 */;
  isPhraseSaved: boolean;
  /**
   * Optional: Only sent from feed view to track lesson interactions
   */
  trackLessonInteraction?: LessonInteractionInfo;
}
export interface PutUserPhraseStatusRequest {
  phraseId: number /* int64 */;
  phraseStatus: 'known' | 'learning';
}
/**
 * WordTranslation is an optional translation for words without a definitionId (e.g. Flight School).
 * When provided, the backend will find-or-create the definition.
 */
export interface WordTranslation {
  targetWord: string;
  nativeWord: string;
}
export interface PutWordStatusRequest {
  wordKey: string;
  newStatus: 'new' | 'learning' | 'known' | 'deleted';
  definitionId: number /* int64 */;
  lessonId: number /* int64 */;
  /**
   * Optional: translation for words without a definitionId (e.g. Flight School)
   */
  translation?: WordTranslation;
  /**
   * Optional: Only sent from feed view to track lesson interactions
   */
  trackLessonInteraction?: LessonInteractionInfo;
}
export interface PutWordStatusBulkRequest {
  wordKeyDefinitionIDPairs: WordKeyDefinitionIDPair[];
  newStatus: 'new' | 'learning' | 'known' | 'deleted';
  lessonId: number /* int64 */;
  /**
   * Optional: Only sent from feed view to track lesson interactions
   */
  trackLessonInteraction?: LessonInteractionInfo;
}
export interface WordKeyDefinitionIDPair {
  wordKey: string;
  definitionId: number /* int64 */;
}
export interface GetAllPhrasesResponse {
  phrases: UserPhrase[];
  nextPage: number /* int */;
  totalCount: number /* int */;
}
export type GetSearchPhrasesResponse = GetAllPhrasesResponse;
export interface UserPhrase {
  id: number /* int64 */;
  phraseId: number /* int64 */;
  text: string;
  translation: string;
  lessonId: number /* int64 */;
  chapterId: number /* int64 */;
  phraseStatus: 'known' | 'learning'; // known or learning.
}
export interface TranslateRequest {
  text: string;
  direction: string; // "es-en" or "en-es"
}
export interface TranslateResponse {
  translation: string;
}
export interface BulkAddItem {
  text: string;
  translation: string;
  status: string; // "learning" or "known"
  lessonId?: number /* int64 */;
  sourceVideoWordId1?: number /* int64 */;
  sourceVideoWordId2?: number /* int64 */;
}
export interface BulkAddRequest {
  items: BulkAddItem[];
}
export interface BulkAddResponse {
  added: number /* int */;
  waterDropletsAwarded: number /* int */;
}

//////////
// source: distractor_export.go

/**
 * Version history:
 *   - v1.0.0: Initial version for distractor cache export/import.
 */
export const DistractorExportVersion = "1.0.0";
export const MinSupportedDistractorExportVersion = "1.0.0";
/**
 * Request/Response types
 */
export interface ExportAllDistractorsResponse {
  version: string;
  distractors: DistractorExportFormat[];
}
export interface ImportDistractorsRequest {
  version: string;
  distractors: DistractorExportFormat[];
}
export interface ImportDistractorsResponse {
  successCount: number /* int */;
  skippedCount: number /* int */;
  warnings?: string[];
}
/**
 * Export Format types - This is the JSON schema for distractor exports
 */
export interface DistractorExportFormat {
  id: number /* int64 */;
  wordKey: string;
  cefrLevel: string;
  distractorLanguage: string;
  distractors: string[];
  nativeWord: string;
  createdAt: any /* time.Time */;
}

//////////
// source: edit_dto.go

/**
 * DTOs
 */
export interface GetCourseInfoWithSizeChaptersRequest {
  courseId: number /* int64 */;
}
export interface GetCourseInfoWithSizeChaptersResponse {
  courseWithSize: CourseWithSize;
  chapters: Chapter[];
}
/**
 * contains size information
 */
export interface CourseWithSize {
  course: Course;
  duration: any /* domain.HHMMSS */;
  totalChapters: number /* int64 */;
}
export interface GetAllCoursesWithSizeResponse {
  courses: CourseWithSize[];
}
export interface PostBroadcastRequest {
  title: string;
  body: string;
}
export interface PostAIQueueChapterRequest {
  chapter: GenChapter;
  forceGenerate: boolean;
}
export interface GenCourse {
  courseName: string;
  description: string;
  chapters: GenChapter[];
}
export interface GenChapter {
  title: string;
  youtubeId: string;
  relativeSequence: number /* int */; // tells us about the order we expect this chapter to be in relative to the other chapters being created
  /**
   * in format HH:MM:SS or MM:SS
   */
  start: string;
  end: string;
  aspectRatio: '16:9' | '9:16';
}
export interface PutChapterRequest {
  chapter: Chapter;
}
export interface PutCourseRequest {
  course: Course;
}
export interface DeleteLessonRequest {
  LessonID: number /* int64 */;
}
export interface DeleteChapterRequest {
  ChapterID: number /* int64 */;
}
export interface PutLessonRequest {
  lesson: Lesson;
}
export interface DeleteVideoWordRequest {
  videoWordId: number /* int64 */;
}
export interface RegenTranslationRequest {
  chapterId: number /* int64 */;
  videoWordId: number /* int64 */;
  promptFeedback: string;
}
export interface RegenTranslationResponse {
  GenericMessageResponse: GenericMessageResponse;
}
export interface PostVideoWordRequest {
  chapterId: number /* int64 */;
  leftVideoWordId: number /* int64 */;
  displayText: string;
  start: number;
  contextStartMs: number;
  contextEndMs: number;
}
export interface PutLessonDataRequest {
  fill: FillActivity;
  guess: GuessActivity;
  write: WriteActivity;
  match: MatchActivity;
  lessonId: number /* int64 */;
  videoStartTime: number /* int */;
  videoEndTime: number /* int */;
  updatedWords: UserVideoWord[];
}
export interface PutVideoWordRequest {
  videoWordId: number /* int64 */;
  displayText: string;
  nativeWord: string;
}
export interface PostCourseRequest {
  course: Course;
}
export interface DeleteCourseRequest {
  CourseID: number /* int64 */;
}
export interface PutTranscriberLoginCookieRequest {
  loginCookie: string;
}
export interface AreCookiesSetResponse {
  areCookiesSet: boolean;
}
export interface AddLessonRequest {
  chapterId: number /* int64 */;
  title: string;
  description: string;
  sequence: number /* int */;
  start: number;
  end: number;
}
export interface AddLessonResponse {
  lessonId: number /* int64 */;
}
export interface SegmentGenerateRequest {
  chapterId: number /* int64 */;
  idealSegmentLength: any /* domain.Seconds */;
}
export interface SegmentGenerateResponse {
  lessonIds: number /* int64 */[];
}
export interface AddSingleSegmentRequest {
  chapterId: number /* int64 */;
  start: number;
  end: number;
}
export interface AddSingleSegmentResponse {
  lessonId: number /* int64 */;
}
/**
 * DTOs for course management operations
 */
export interface ClearDuplicateChaptersRequest {
  courseId: number /* int64 */;
}
export interface ClearDuplicateChaptersResponse {
  message: string;
  deletedCount: number /* int */;
}
export interface ClearAllChaptersRequest {
  courseId: number /* int64 */;
}
export interface ClearAllChaptersResponse {
  message: string;
  deletedCount: number /* int */;
}
export interface TransferEntireCourseRequest {
  sourceCourseId: number /* int64 */;
  targetCourseId: number /* int64 */;
}
export interface TransferEntireCourseResponse {
  message: string;
  transferredCount: number /* int */;
}
export interface GetTranscriptResponse {
  isManual: boolean;
  youtubeId: string;
}
export interface GetAllYoutubeVideosResponse {
  youtubeIds: string[];
}
export interface GetAllCourseTopicsResponse {
  topics: CourseTopic[];
}
export interface GetTranslationReportResponse {
  translationReport: TranslationReport;
}
export interface ApplyTranslationReportFixesRequest {
  chapterId: number /* int64 */;
  report: TranslationReport;
}
export interface GetChapterActivitiesResponse {
  lessons: LessonWithActivities[];
}
/**
 * whichover is not empty gets updated
 */
export interface UpdateActivityRequest {
  lessonId: number /* int64 */;
  lessonActivities: LessonActivities;
  fieldToUpdate: 'fill' | 'guess' | 'match' | 'write' | 'video' | 'full_video';
}
export interface LessonWithActivities {
  lesson: Lesson;
  activities: LessonActivities;
}
export interface LessonActivities {
  fill?: FillActivity;
  guess?: GuessActivity;
  match?: MatchActivity;
  write?: WriteActivity;
}
/**
 * TranslationReportItem represents an item in the translation report
 */
export interface TranslationReportItem {
  videoWordId: number /* int */;
  sentence: string;
  nativeWord: string;
  targetWord: string;
  isCorrect: boolean;
  correction: string;
  reasoning: string;
  error?: string;
}
/**
 * TranslationReport represents a collection of translation report items
 */
export interface TranslationReport {
  items: TranslationReportItem[];
}
export interface GetAllCourseLevelsResponse {
  levels: CourseLevel[];
}
export interface AssignTopicToCourseRequest {
  courseId: number /* int64 */;
  topicId: number /* int64 */;
}
export interface AssignLevelToCourseRequest {
  courseId: number /* int64 */;
  levelId: number /* int64 */;
}
export interface CreateCourseTopicRequest {
  courseTopic: CourseTopic;
}
export interface CreateCourseTopicResponse {
  id: number /* int64 */;
  message: string;
}
export interface UpdateCourseTopicRequest {
  courseTopic: CourseTopic;
}
export interface CreateCourseLevelRequest {
  courseLevel: CourseLevel;
}
export interface UpdateCourseLevelRequest {
  courseLevel: CourseLevel;
}
export interface CreateCourseLevelResponse {
  id: number /* int64 */;
  message: string;
}
export interface GetChannelVideoUrlsResponse {
  urls: string[];
}
/**
 * ChapterInfoWithQAInfo DTO for tracking chapter QA metrics
 */
export interface ChapterInfoWithQAInfo {
  chapterId: number /* int64 */;
  title: string;
  description: string;
  youtubeId: string;
  start: number;
  end: number;
  aspectRatio: '16:9' | '9:16';
  sequence: number /* int */;
  published: boolean;
  isFullVidHidden: boolean;
  videoLevel: number /* float64 */;
  cefrLevel: 'No Level' | 'A0' | 'A0 - Scratch' | 'Lower A1' | 'Upper A1' | 'Lower A2' | 'Upper A2' | 'Lower B1' | 'Upper B1' | 'Lower B2' | 'Upper B2' | 'Lower C1' | 'Upper C1' | 'C2';
  qaStatus: QAStatus;
  activitiesPending: number /* int64 */;
  activitiesApproved: number /* int64 */;
  lessonsEdited: number /* int64 */;
  totalLessons: number /* int64 */;
  isManualSetLevel: boolean;
  topics: { id: number /* int64 */; Name: string; Confidence: number /* float64 */; }[];
  dialect: 'latam' | 'spain' | 'unknown';
  cloudflareId?: string;
  player: 'unset' | 'cloudflare' | 'youtube';
  assignedTo: string;
}
export interface GetChapterWithQAInfoRequest {
  chapterId: number /* int64 */;
}
export interface GetChapterWithQAInfoResponse {
  chapter: ChapterInfoWithQAInfo;
}
export interface PutChapterQAStatusRequest {
  chapterId: number /* int64 */;
  qaStatus: string;
}
export interface PutChapterAssignedToRequest {
  chapterId: number /* int64 */;
  assignedTo: string;
}
export interface GetAssigneesResponse {
  assignees: string[];
}
export interface ChapterLevel {
  wpmLevel?: number /* float64 */;
  speakingSpeed?: number /* float64 */;
  textLevel?: number /* float64 */;
  finalLevel: number /* float64 */;
  finalCEFR: 'No Level' | 'A0' | 'A0 - Scratch' | 'Lower A1' | 'Upper A1' | 'Lower A2' | 'Upper A2' | 'Lower B1' | 'Upper B1' | 'Lower B2' | 'Upper B2' | 'Lower C1' | 'Upper C1' | 'C2';
}
export type CEFRLevel = 'No Level' | 'A0' | 'A0 - Scratch' | 'Lower A1' | 'Upper A1' | 'Lower A2' | 'Upper A2' | 'Lower B1' | 'Upper B1' | 'Lower B2' | 'Upper B2' | 'Lower C1' | 'Upper C1' | 'C2';
/**
 * SearchType represents the type of search to perform
 */
export type SearchType = string;
export const SearchTypeChapterByYoutubeID: SearchType = "chapter_by_youtubeid";
export const SearchTypeChapterByChapterID: SearchType = "chapter_by_chapterid";
export const SearchTypeChapterByTitle: SearchType = "chapter_by_title";
export const SearchTypeCourseByTitle: SearchType = "course_by_title";
export const SearchTypeCourseByCourseID: SearchType = "course_by_courseid";
export const SearchTypeLessonByLessonID: SearchType = "lesson_by_lessonid";
/**
 * Global Settings DTOs
 */
export interface GetGlobalSettingsResponse {
  globalPrimaryPlayer: string;
  explainerModel: string;
  explainerEnabled: boolean;
  showZeroPriceTrialText: boolean;
  showAnnualPricePaywall: boolean;
  wordlistMinLevel: string;
}
export interface PutGlobalSettingsRequest {
  globalPrimaryPlayer?: string;
  explainerModel?: string;
  explainerEnabled?: boolean; // Pointer to distinguish between not set and false
  showZeroPriceTrialText?: boolean; // Pointer to distinguish between not set and false
  showAnnualPricePaywall?: boolean; // Pointer to distinguish between not set and false
  wordlistMinLevel?: string; // Pointer to distinguish between not set and empty
  password?: string;
}
export interface AppConfigResponse {
  showZeroPriceTrialText: boolean;
  showAnnualPricePaywall: boolean;
  wordlistMinLevel: string;
}
/**
 * Cloudflare Backfill DTOs
 */
export interface CloudflareBackfillResponse {
  successes: CloudflareBackfillSuccess[];
  failures: CloudflareBackfillFailure[];
}
export interface CloudflareBackfillSuccess {
  chapterId: number /* int64 */;
  title: string;
  youtubeId: string;
  cloudflareId: string;
}
export interface CloudflareBackfillFailure {
  chapterId: number /* int64 */;
  title: string;
  youtubeId: string;
  error: string;
}
/**
 * Chapter Backup Status DTOs
 */
export interface GetChapterBackupStatusRequest {
  Page: number /* int */;
  Limit: number /* int */;
  FilterMode: string; // "all", "with", "without"
}
export interface ChapterBackupStatus {
  chapterId: number /* int64 */;
  title: string;
  youtubeId: string;
  cloudflareId: string; // empty string if not set
  hasCloudflareId: boolean;
  player: string;
}
export interface GetChapterBackupStatusResponse {
  chapters: ChapterBackupStatus[];
  totalCount: number /* int */;
  page: number /* int */;
  limit: number /* int */;
  hasMore: boolean;
}
export interface GetCloudflareBackupStatsResponse {
  totalPublishedChapters: number /* int */;
  publishedChaptersWithBackup: number /* int */;
  totalChapters: number /* int */;
  chaptersWithBackup: number /* int */;
}
export interface GetChapterLevelResponse {
  chapterLevel: ChapterLevel;
}
/**
 * SearchResult represents a search result from the global search functionality
 */
export interface SearchResult {
  type: SearchType;
  chapterSearchResult?: ChapterSearchResult;
  lessonSearchResult?: LessonSearchResult;
  courseSearchResult?: CourseSearchResult;
}
export interface ChapterSearchResult {
  chapter: Chapter;
  courseId: number /* int64 */;
}
export interface LessonSearchResult {
  lesson: Lesson;
  chapterId: number /* int64 */;
  courseId: number /* int64 */;
}
export interface CourseSearchResult {
  course: Course;
}
/**
 * SearchResponse represents the response for global search
 */
export interface SearchResponse {
  results: SearchResult[];
  nextPage: number /* int */;
  totalCount: number /* int */;
}
/**
 * ReorderChaptersRequest represents the request to reorder chapters in a course
 */
export interface ReorderChaptersRequest {
  courseId: number /* int64 */;
  chapterOrder: number /* int64 */[];
}
/**
 * MoveChapterToPositionRequest represents the request to move a chapter to a specific position
 */
export interface MoveChapterToPositionRequest {
  chapterId: number /* int */;
  position: number /* int */;
}

//////////
// source: email_dto.go

/**
 * EmailQueueItem DTO
 */
export interface EmailQueueItem {
  id: number /* int64 */;
  userId: number /* int64 */;
  templateType: EmailTemplateType;
  emailSendStatus: EmailSendStatus;
  createdAt: any /* time.Time */;
  sentAt: any /* time.Time */;
  scheduledAt: any /* time.Time */;
  failureReason: string;
  isTestEmail: boolean;
}
export type EmailSendStatus = 'in_queue' | 'sent' | 'failed';
/**
 * EmailTemplate DTO
 */
export interface EmailTemplate {
  templateType: EmailTemplateType;
  body: string;
  subject: string;
  isEnabled: boolean;
  fromAddress?: string;
  replyToAddress?: string;
  senderName?: string;
}
export type EmailTemplateType = 'welcome' | 'app_use_feedback' | 'churned_user_feedback' | 'custom_email';

//////////
// source: email_requests.go

/**
 * SendEmailRequest represents the request body for sending an email
 */
export interface SendEmailRequest {
  toUserId: number /* int64 */;
  scheduleAt: string; // UTC timestamp in RFC3339 format
  templateType: EmailTemplateType;
}
/**
 * SendTestEmailRequest represents the request body for sending a test email
 */
export interface SendTestEmailRequest {
  toUserId: number /* int64 */;
  templateType: EmailTemplateType;
}
/**
 * SaveTemplateRequest represents the request body for saving an email template
 */
export interface SaveTemplateRequest {
  template: EmailTemplate;
}
/**
 * GetTemplatesResponse represents the response for fetching email templates
 */
export interface GetTemplatesResponse {
  templates: EmailTemplate[];
}
/**
 * GetEmailQueueItemsRequest represents the request for fetching email queue items with filters
 */
export interface GetEmailQueueItemsRequest {
  status?: EmailSendStatus; // Filter by status
  page?: number /* int */;
  limit?: number /* int */;
}
/**
 * GetEmailQueueItemsResponse represents the response for email queue items with pagination
 */
export interface GetEmailQueueItemsResponse {
  items: EmailQueueItem[];
  nextPage: number /* int */;
  totalCount: number /* int */;
}
/**
 * EmailNoSendEntry represents an email that should never receive automated emails
 */
export interface EmailNoSendEntry {
  id: number /* int64 */;
  email: string;
  reason: string;
  createdAt: any /* time.Time */;
}
/**
 * AddToNoSendListRequest represents the request to add emails to the no-send list
 */
export interface AddToNoSendListRequest {
  emails: string[]; // List of emails to add
  reason: string; // Optional reason for blocking
}
/**
 * AddToNoSendListResponse represents the response after adding emails to the no-send list
 */
export interface AddToNoSendListResponse {
  added: string[]; // Emails successfully added
  skipped: string[]; // Emails that were already in the list
  message: string;
}
/**
 * RemoveFromNoSendListRequest represents the request to remove an email from the no-send list
 */
export interface RemoveFromNoSendListRequest {
  email: string;
}
/**
 * GetNoSendListResponse represents the response containing all blocked emails
 */
export interface GetNoSendListResponse {
  entries: EmailNoSendEntry[];
}

//////////
// source: embed_check_dto.go

export interface NotEmbeddableVideo {
  youtubeId: string;
  checkedAt: string;
  chapterId?: number /* int64 */;
  chapterName?: string;
  published?: boolean;
}
export interface EmbedLogEntry {
  id: number /* int64 */;
  youtubeId: string;
  chapterId?: number /* int64 */;
  userId?: string;
  errorSource: string;
  createdAt: string;
}
export interface GetNotEmbeddableResponse {
  videos: NotEmbeddableVideo[];
}
export interface GetEmbedLogsResponse {
  logs: EmbedLogEntry[];
}

//////////
// source: explainer_dto.go

/**
 * ExplainRequest contains the request data for AI word/phrase explanation
 */
export interface ExplainRequest {
  word: string; // The Spanish word or phrase
  translation: string; // The English translation
  context: string; // The sentence containing the word
  conversationHistory: ExplainChatMessage[]; // Previous messages in conversation
  isPhrase: boolean; // Whether this is a phrase or single word
}
/**
 * ExplainChatMessage represents a message in the conversation history
 */
export interface ExplainChatMessage {
  role: string; // "user" or "assistant"
  content: string;
}
/**
 * ExplainStreamEvent represents a Server-Sent Event for streaming
 */
export interface ExplainStreamEvent {
  type: string; // "chunk", "complete", "error"
  content?: string; // The content chunk or full content
  error?: string; // Error message if type is "error"
  /**
   * Metadata fields (only in "complete" event)
   */
  inputTokens?: number /* int */;
  outputTokens?: number /* int */;
  model?: string;
  durationMs?: number /* int64 */;
  remainingMessages?: number /* int */;
}

//////////
// source: feedback_dto.go

/**
 * Read AI Webhook Payload
 */
export interface ReadAIWebhookPayload {
  session_id: string;
  trigger: string;
  title: string;
  start_time: string;
  end_time: string;
  participants: ReadAIParticipant[];
  owner: ReadAIParticipant;
  summary: string;
  action_items: ReadAIActionItem[];
  key_questions: ReadAIKeyQuestion[];
  topics: ReadAITopic[];
  report_url: string;
  transcript: ReadAITranscript;
}
export interface ReadAIParticipant {
  name: string;
  email: string;
}
export interface ReadAIActionItem {
  text: string;
}
export interface ReadAIKeyQuestion {
  text: string;
}
export interface ReadAITopic {
  text: string;
}
export interface ReadAITranscript {
  speakers: ReadAISpeaker[];
  speaker_blocks: ReadAISpeakerBlock[];
}
export interface ReadAISpeaker {
  name: string;
}
export interface ReadAISpeakerBlock {
  start_time: number /* int64 */; // Unix timestamp in milliseconds
  end_time: number /* int64 */; // Unix timestamp in milliseconds
  speaker: ReadAISpeaker;
  words: string;
}
/**
 * Manual Feedback Request
 */
export interface ManualFeedbackRequest {
  user_name: string;
  user_email: string;
  source_type: string; // call, email, nps, app_store_review, whatsapp, other, bug_report, usage
  source_date: string; // YYYY-MM-DD or RFC3339
  raw_content: string;
}
export interface ManualFeedbackResponse {
  message: string;
  source_id: number /* int64 */;
  extracted_items: number /* int */;
}
/**
 * Feedback Source Response
 */
export interface FeedbackSourceResponse {
  id: number /* int64 */;
  user_name: string;
  user_email: string;
  source_type: string;
  source_date: any /* time.Time */;
  tags: string[];
  item_count: number /* int */;
}
/**
 * Feedback Source Detail Response
 */
export interface FeedbackSourceDetailResponse {
  id: number /* int64 */;
  user_name: string;
  user_email: string;
  source_type: string;
  source_date: any /* time.Time */;
  raw_content: string;
  read_ai_session_id?: string;
  read_ai_report_url?: string;
  summary?: string;
  action_items?: string[];
  key_questions?: string[];
  topics?: string[];
  created_at: any /* time.Time */;
  items: FeedbackItemResponse[];
}
/**
 * Feedback Item Response
 */
export interface FeedbackItemResponse {
  id: number /* int64 */;
  category: string;
  feedback_text: string;
  tag: string;
  context_snippet?: string;
  created_at: any /* time.Time */;
}
/**
 * Tag Summary Response
 */
export interface TagSummaryResponse {
  tag_name: string;
  count: number /* int */;
  primary_category: string;
  category_breakdown: { [key: string]: number /* int */};
  last_used_at: any /* time.Time */;
}
/**
 * Get Feedback Sources Request (query parameters)
 */
export interface GetFeedbackSourcesRequest {
  Limit: number /* int */;
  Offset: number /* int */;
  SourceTypeFilter: string;
  TagFilter: string;
  SearchQuery: string;
  StartDate: string;
  EndDate: string;
}
/**
 * Get Feedback Sources Response
 */
export interface GetFeedbackSourcesResponse {
  sources: FeedbackSourceResponse[];
  total_count: number /* int */;
  limit: number /* int */;
  offset: number /* int */;
}
/**
 * Feedback Stats Response
 */
export interface FeedbackStatsResponse {
  total_items: number /* int */;
  total_sources: number /* int */;
  category_counts: { [key: string]: number /* int */};
  top_tags: TagSummaryResponse[];
}
/**
 * Tag Detail Response (for clicking on a tag)
 */
export interface TagDetailResponse {
  tag_name: string;
  total_mentions: number /* int */;
  category_breakdown: { [key: string]: number /* int */};
  items: TagDetailItem[];
}
export interface TagDetailItem {
  feedback_item_id: number /* int64 */;
  feedback_source_id: number /* int64 */;
  category: string;
  feedback_text: string;
  context_snippet?: string;
  user_name: string;
  user_email: string;
  source_type: string;
  source_date: any /* time.Time */;
}
/**
 * UpdateFeedbackItemRequest is the request body for updating a feedback item's text, tag, and/or category
 */
export interface UpdateFeedbackItemRequest {
  feedback_text: string;
  tag: string;
  category: string;
}
/**
 * UpdateFeedbackSourceRequest is the request body for updating a feedback source's details
 */
export interface UpdateFeedbackSourceRequest {
  user_name: string;
  user_email: string;
  source_type: string;
  source_date: string; // format: YYYY-MM-DD
}
/**
 * BugReportRequest is the request body for in-app bug report submission
 */
export interface BugReportRequest {
  description: string;
  name: string;
}
/**
 * BugReportResponse is the response body for in-app bug report submission
 */
export interface BugReportResponse {
  message: string;
}
/**
 * CreateFeedbackItemRequest is the request body for creating a new feedback item
 */
export interface CreateFeedbackItemRequest {
  category: string;
  feedback_text: string;
  tag: string;
  context_snippet?: string;
}
/**
 * AppStoreReview represents a single App Store review in the import payload
 */
export interface AppStoreReview {
  reviewer_name: string;
  title: string;
  body: string;
  rating: number /* int */;
  date: string; // YYYY-MM-DD format
}
/**
 * ImportAppStoreReviewsRequest is the request body for bulk importing App Store reviews
 */
export interface ImportAppStoreReviewsRequest {
  reviews: AppStoreReview[];
}
/**
 * ImportAppStoreReviewsResponse is the response after importing App Store reviews
 */
export interface ImportAppStoreReviewsResponse {
  message: string;
  sources_created: number /* int */;
  skipped: number /* int */;
}

//////////
// source: flight_school_dto.go

/**
 * CompleteFlightSchoolModuleRequest is the request for marking a module as complete
 */
export interface CompleteFlightSchoolModuleRequest {
  moduleId: string;
}
/**
 * GetFlightSchoolProgressResponse is the response for getting flight school progress
 */
export interface GetFlightSchoolProgressResponse {
  completedModules: string[];
  /**
   * CurrentModuleID is the user's persisted skip-ahead pointer (the module
   * they jumped to without completing the ones before it). Empty when the
   * user has never skipped — the client then derives "current" as the first
   * incomplete module.
   */
  currentModuleId: string;
}
/**
 * FlightSchoolModuleConfigResponse is the full admin response for a config version.
 */
export interface FlightSchoolModuleConfigResponse {
  id: number /* int64 */;
  version: number /* int */;
  config: any /* json.RawMessage */;
  changelog: string;
  isActive: boolean;
  createdBy: string;
  createdAt: any /* time.Time */;
}
/**
 * FlightSchoolModuleConfigPublicResponse is the public (no-auth) response.
 */
export interface FlightSchoolModuleConfigPublicResponse {
  config: any /* json.RawMessage */;
  version: number /* int */;
}
/**
 * FlightSchoolModuleConfigVersionSummary is a lightweight version entry for listing.
 */
export interface FlightSchoolModuleConfigVersionSummary {
  id: number /* int64 */;
  version: number /* int */;
  changelog: string;
  isActive: boolean;
  createdBy: string;
  createdAt: any /* time.Time */;
}
/**
 * CreateFlightSchoolModuleConfigRequest is the request body for creating a new config version.
 */
export interface CreateFlightSchoolModuleConfigRequest {
  config: any /* json.RawMessage */;
  changelog: string;
}

//////////
// source: game_dto.go

/**
 * Number of droplets awarded when an activity is completed (at most one per day)
 */
export const ActivityDroplets = 3;
/**
 * Number of droplets awarded when a lesson is completed for the first time
 */
export const LessonDroplets = 6;
/**
 * Number of droplets awarded when a chapter is completed for the first time
 */
export const ChapterDroplets = 14;
/**
 * Number of droplets awarded when a course is completed for the first time
 */
export const CourseDroplets = 50;
/**
 * Number of droplets awarded when a word status changes from new to learning
 */
export const PutWordDroplets = 3;
/**
 * Number of droplets awarded when a card is marked as good
 */
export const CardGoodDroplets = 3;

//////////
// source: generation_job_dto.go

export type GenerationJobStatus = any /* domain.GenerationJobStatus */;
export interface GenerationJob {
  id: number /* int64 */;
  createdAt: any /* time.Time */;
  updatedAt: any /* time.Time */;
  youtubeId: string;
  workflowId: string;
  runId: string;
  status: GenerationJobStatus;
  currentStep: string;
  stepNumber: number /* int */;
  totalSteps: number /* int */;
  errorMessage: string;
  failedStep: string;
  aspectRatio: string;
  forceGenerate: boolean;
  chapterId: number /* int64 */;
  startedAt?: any /* time.Time */;
  completedAt?: any /* time.Time */;
}
export interface PaginatedGenerationJobsResponse {
  jobs: GenerationJob[];
  totalCount: number /* int */;
  page: number /* int */;
  pageSize: number /* int */;
}

//////////
// source: job_dto.go

export interface JobActionResponse {
  success: boolean;
  message: string;
}

//////////
// source: json_export.go

/**
 * Version history:
 *   - v1.1.0: Added duration to words. Disallow old imports to prevent duration overrides.
 *   - v2.0.0: Added hasActivities field to ChapterInfo for V1/V2 API compatibility.
 *     Old imports (v1.1.0) will default hasActivities to true for backwards compatibility.
 *     Also added assignedTo field for QA editor assignment (backwards compatible - ignored by older versions).
 */
export const ChapterExportVersion = "2.0.0";
export const MinSupportedExportVersion = "1.1.0";
/**
 * Request/Response types
 */
export interface ExportChapterRequest {
  chapterId: number /* int64 */;
}
export interface ExportAllChaptersResponse {
  chapters: ChapterExportFormat[];
}
export interface ExportChapterResponse {
  chapter: ChapterExportFormat;
}
export interface ImportChaptersRequest {
  version: string;
  courseId: number /* int64 */;
  /**
   * optional field that overrides course id to import to a system course
   */
  systemCourseName: string;
  chapters: ChapterExportFormat[];
}
export interface ImportChaptersResponse {
  chapterIds: number /* int64 */[];
}
/**
 * Export Format types - This is the JSON schema for chapter exports
 */
export interface ChapterExportFormat {
  /**
   * We need to be careful about importing/ exporting version compatibility
   */
  exportVersion: string;
  info: ChapterInfoExport;
  fullVideoActivity: VideoActivityExport;
  chapterLevel: ChapterLevelExport;
  lessons: LessonExport[];
  youtubeMetadata?: YouTubeMetadataExport;
}
export interface ChapterInfoExport {
  title: string;
  description: string;
  published: boolean;
  isFullVidHidden: boolean;
  videoLevel: number /* float64 */;
  cefrLevel: string;
  qaStatus: string;
  isManualSetLevel: boolean;
  topics: ChapterTopicExport[];
  dialect: string;
  hasActivities: boolean; // V2 API: false = client-side activities only
  cloudflareId?: string;
  player: 'unset' | 'cloudflare' | 'youtube';
  audioUrl?: string;
  assignedTo: string;
  likeCount: number /* int */;
  videoDisplayMode: any /* domain.VideoDisplayMode */;
}
export interface YouTubeMetadataExport {
  views: number /* int */;
  likes: number /* int */;
  subscribers: number /* int */;
  youtuber?: string;
  handleName?: string;
}
export interface ChapterTopicExport {
  name: string;
  confidence: number /* float64 */;
}
export interface ChapterLevelExport {
  wpmLevel?: number /* float64 */;
  speakingSpeed?: number /* float64 */;
  textLevel?: number /* float64 */;
  finalLevel: number /* float64 */;
  finalCEFR: string;
}
export interface VideoActivityExport {
  videoWords: VideoWordExport[];
  youtubeId: string;
  start: number;
  end: number;
  aspectRatio: '16:9' | '9:16';
}
export interface VideoWordExport {
  start: number;
  /**
   * OPTIONAL VALUE if its not set it will have a value of 0
   */
  duration: number;
  clickHint: string; // deprecated: always empty, kept for backwards compatibility
  sequence: number /* int */;
  displayText: string;
  targetWord: string;
  nativeWord: string;
}
export interface LessonExport {
  info: LessonInfoExport;
  activities: LessonActivitiesExport;
  videoActivity: VideoActivityInfoExport;
}
export interface LessonInfoExport {
  title: string;
  description: string;
  sequence: number /* int */;
  isEdited: boolean;
}
export interface LessonActivitiesExport {
  fill: FillActivity;
  guess: GuessActivity;
  write: WriteActivity;
  match: MatchActivity;
}
export interface VideoActivityInfoExport {
  youtubeId: string;
  start: number;
  end: number;
  aspectRatio: '16:9' | '9:16';
}

//////////
// source: middlewear.go

export const ClientUpdateRequiredMessage = "CLIENT UPDATE REQUIRED";
/**
 * defined for easy use in the frontend
 */
export const ClientUpdateHeaderKey = "Client-Version";
export const ClientSessionHeader = "X-Client-Session-ID";
/**
 * FlightSchoolActivitiesHeaderKey lets a client advertise which Flight School
 * activity types it can render, as a comma-separated list. The public config
 * endpoint strips activities whose type is not advertised, so newer activity
 * types (e.g. "write") are only served to clients that support them and the
 * config stays backwards-compatible for already-shipped clients that omit the
 * header.
 */
export const FlightSchoolActivitiesHeaderKey = "X-Flight-School-Activities";
/**
 * Device telemetry headers — shared with all clients so adding/renaming one
 * is a single-source-of-truth change. Mirrored to shared_types/index.ts by
 * tygo on server startup so JS clients can import them. We deliberately do
 * NOT capture the user-chosen device name (e.g. "Erik's iPhone") since it
 * frequently contains PII.
 */
export const DevicePlatformHeader = "X-Device-Platform";
/**
 * Device telemetry headers — shared with all clients so adding/renaming one
 * is a single-source-of-truth change. Mirrored to shared_types/index.ts by
 * tygo on server startup so JS clients can import them. We deliberately do
 * NOT capture the user-chosen device name (e.g. "Erik's iPhone") since it
 * frequently contains PII.
 */
export const DeviceModelHeader = "X-Device-Model";
/**
 * Device telemetry headers — shared with all clients so adding/renaming one
 * is a single-source-of-truth change. Mirrored to shared_types/index.ts by
 * tygo on server startup so JS clients can import them. We deliberately do
 * NOT capture the user-chosen device name (e.g. "Erik's iPhone") since it
 * frequently contains PII.
 */
export const DeviceModelNameHeader = "X-Device-Model-Name";
/**
 * Device telemetry headers — shared with all clients so adding/renaming one
 * is a single-source-of-truth change. Mirrored to shared_types/index.ts by
 * tygo on server startup so JS clients can import them. We deliberately do
 * NOT capture the user-chosen device name (e.g. "Erik's iPhone") since it
 * frequently contains PII.
 */
export const OSVersionHeader = "X-OS-Version";
/**
 * Device telemetry headers — shared with all clients so adding/renaming one
 * is a single-source-of-truth change. Mirrored to shared_types/index.ts by
 * tygo on server startup so JS clients can import them. We deliberately do
 * NOT capture the user-chosen device name (e.g. "Erik's iPhone") since it
 * frequently contains PII.
 */
export const OTAUpdateIDHeader = "X-OTA-Update-Id";

//////////
// source: nps_dto.go

/**
 * NPSRatingRequest represents the request body for submitting an NPS rating
 */
export interface NPSRatingRequest {
  rating: number /* int */;
  isSkipScore: boolean;
}
/**
 * NPSFeedbackRequest represents the request body for submitting feedback for an NPS rating
 */
export interface NPSFeedbackRequest {
  feedback: string;
}
export interface GetNPSFeedbackRequest {
  startDate: any /* time.Time */;
  endDate: any /* time.Time */;
}
/**
 * NPSResponse represents a Net Promoter Score response in API responses
 */
export interface NPSResponse {
  id: number /* int64 */;
  userId: number /* int64 */;
  score: number /* int */;
  feedback: string;
  createdAt: any /* time.Time */;
  isFollowedUp: boolean;
  email: string;
  displayName: string;
  isSkipScore: boolean;
  isContacted: boolean;
  userJoinDate: any /* time.Time */;
  placementResult: string;
  onboardingLevel: string;
}
/**
 * NPSCompletedResponse represents the response for the "is NPS completed" check
 */
export interface NPSCompletedResponse {
  shouldSeeNPS: boolean;
}
/**
 * AppRatingEligibilityResponse represents the response for app rating eligibility check
 */
export interface AppRatingEligibilityResponse {
  shouldSeeAppRating: boolean;
}
/**
 * NPSResponseListResponse represents a list of NPS responses
 */
export interface NPSResponseListResponse {
  responses: NPSResponse[];
}
export interface MarkNPSFollowedUpRequest {
  responseId: number /* int64 */;
}
export interface UpdateNPSContactedRequest {
  responseId: number /* int64 */;
  isContacted: boolean;
}

//////////
// source: partner_admin_dto.go

/**
 * DTOs
 */
export interface Partner {
  id: number /* int64 */;
  user_id: number /* int64 */;
  name: string;
  code: string;
}
export interface PartnerWithCounts {
  partner: Partner;
  link_click_count: number /* int */;
  temp_user_signup_count: number /* int */;
  free_user_count: number /* int */;
  trial_user_count: number /* int */;
  premium_user_count: number /* int */;
}
export interface PartnerStats {
  partner_id: number /* int64 */;
  all_refered_user_ids: number /* int64 */[];
  paid_user_ids: number /* int64 */[];
}
/**
 * Request/Response types
 */
export interface RegisterPartnerRequest {
  name: string;
  code: string;
  user_id: number /* int64 */; // it must get linked to an existing user
}
export interface ListPartnersResponse {
  partners: PartnerWithCounts[];
}
export interface PartnerStatsRequest {
  partner_id: number /* int64 */;
}
export interface AdminPartnerStatsResponse {
  partner: PartnerWithCounts;
  users: AdminUserStatResult[];
}
export interface PartnerStatsResponse {
  partner: PartnerWithCounts;
}
export interface UpdatePartnerRequest {
  id: number /* int64 */;
  partner_name: string;
  code: string;
  user_id: number /* int64 */;
}
export interface DeletePartnerRequest {
  id: number /* int64 */;
}

//////////
// source: path_progress_dto.go

/**
 * PathProgressResponse mirrors domain.PathProgress with camelCase JSON tags
 * for the /auth/path/progress endpoint.
 */
export interface PathProgressResponse {
  currentPathLevel: string;
  currentSectionIndex: number /* int */;
  currentCourseId: number /* int64 */;
  currentChapterId: number /* int64 */;
  placementResult: CEFRLevel;
  sections: PathProgressSectionDTO[];
}
/**
 * PathProgressSectionDTO mirrors domain.PathProgressSection for JSON output.
 */
export interface PathProgressSectionDTO {
  pathLevel: string;
  sectionIndex: number /* int */;
  globalIndex: number /* int */;
  courseId: number /* int64 */;
  displayName: string;
  totalChapters: number /* int */;
  completedChapters: number /* int */;
  isCompleted: boolean;
  currentChapterId: number /* int64 */;
  chapterIds: number /* int64 */[];
  completedChapterIds: number /* int64 */[];
}
/**
 * PathSkipRequest is the body for POST /auth/path/skip.
 */
export interface PathSkipRequest {
  /**
   * TargetGlobalIndex is the global section index (0..N-1 across all path
   * levels) the user wants to skip ahead to. Must be after the user's
   * current section and within the same path level.
   */
  targetGlobalIndex: number /* int */;
}
/**
 * PathSkipToChapterRequest is the body for POST /auth/path/skip-to-chapter.
 */
export interface PathSkipToChapterRequest {
  /**
   * TargetChapterID is the published chapter the user wants to make their
   * current node. Must be a forward, same-path-level chapter (later section,
   * or a later chapter in the current section) and not already completed.
   */
  targetChapterId: number /* int64 */;
}
/**
 * PathSkipToFsModuleRequest is the body for POST /auth/path/skip-to-fs-module.
 */
export interface PathSkipToFsModuleRequest {
  /**
   * ModuleID is the Flight School module the user wants to make their
   * current node. Must be a published module, forward of their current
   * module, and not already completed. The skipped-over modules stay
   * incomplete ("skipped").
   */
  moduleId: string;
}

//////////
// source: placement_config_dto.go

/**
 * PlacementConfigResponse is the full config returned to clients and admin.
 */
export interface PlacementConfigResponse {
  id: number /* int64 */;
  version: number /* int */;
  config: any /* json.RawMessage */;
  changelog: string;
  isActive: boolean;
  createdBy: string;
  createdAt: any /* time.Time */;
}
/**
 * PlacementConfigPublicResponse is the config returned via the public endpoint (no metadata).
 */
export interface PlacementConfigPublicResponse {
  config: any /* json.RawMessage */;
  version: number /* int */;
}
/**
 * PlacementConfigVersionSummary is a lightweight version listing.
 */
export interface PlacementConfigVersionSummary {
  id: number /* int64 */;
  version: number /* int */;
  changelog: string;
  isActive: boolean;
  createdBy: string;
  createdAt: any /* time.Time */;
}
/**
 * CreatePlacementConfigRequest is the request body for creating a new config version.
 */
export interface CreatePlacementConfigRequest {
  config: any /* json.RawMessage */;
  changelog: string;
}
/**
 * CheckEmbedsRequest is the request body for checking embed status.
 */
export interface CheckEmbedsRequest {
  youtubeIds: string[];
}
/**
 * CheckEmbedsResponse returns embed check results.
 */
export interface CheckEmbedsResponse {
  results: { [key: string]: boolean};
}
/**
 * PlacementNodeEventRequest is sent by the client for each node visited during placement test.
 */
export interface PlacementNodeEventRequest {
  configVersion: number /* int */;
  nodeId: string;
  answer: string;
  stepIndex: number /* int */;
}
/**
 * SimulatePlacementEventsRequest is for the dev endpoint to generate test data.
 */
export interface SimulatePlacementEventsRequest {
  configVersion: number /* int */;
  userCount: number /* int */;
}
/**
 * PlacementAnalyticsResponse is the response for per-node analytics on a config version.
 */
export interface PlacementAnalyticsResponse {
  configVersion: number /* int */;
  totalUsers: number /* int */;
  nodes: { [key: string]: NodeAnalytics};
}
/**
 * NodeAnalytics holds per-node metrics.
 */
export interface NodeAnalytics {
  userCount: number /* int */;
  pctOfTotal: number /* float64 */;
  hardCount: number /* int */;
  justRightCount: number /* int */;
  easyCount: number /* int */;
}
/**
 * LessonInfoResponse holds enriched lesson information for admin display.
 */
export interface LessonInfoResponse {
  lessonId: number /* int64 */;
  lessonName: string;
  chapterName: string;
  chapterId: number /* int64 */;
  youtubeId: string;
  cloudflareId?: string;
  player: string;
  creatorName?: string;
  creatorId?: string;
  isLicensed?: boolean;
  isEmbeddable?: boolean;
  published: boolean;
}

//////////
// source: shared_dto.go

export interface GenericMessageResponse {
  message: string;
}
export interface TransferChapterRequest {
  chapterId: number /* int64 */;
  newCourseId: number /* int64 */;
  oldCourseId: number /* int64 */;
  /**
   * optional value to move the old courseID to the system course name.
   */
  systemName?: SystemCourseName;
}
export type SystemCourseName = string;
export const SystemCourseNameQueue: SystemCourseName = "queue";
export const SystemCourseNameBad: SystemCourseName = "bad";
export const SystemCourseNameEdited: SystemCourseName = "edited";
export const SystemCourseNameExported: SystemCourseName = "exported";
export const SystemCourseNameTriage: SystemCourseName = "triage";
export interface PostForceAlignChapterRequest {
  chapterId: number /* int64 */;
}
export interface UpdateChapterLevelRequest {
  chapterId: number /* int64 */;
}
export interface UpdateChapterTopicsRequest {
  chapterId: number /* int64 */;
}
export interface ErrorResponse {
  error: string;
}
export interface WaterDropletResponse {
  message: string;
  waterDropletsAwarded: number /* int */;
}

//////////
// source: stats_dto.go

/**
 * DTOs
 */
export interface GetPlantStatsResponse {
  chaptersCompleted: number /* int */;
  waterDropletsNeeded: number /* int */;
  waterDropletsCount: number /* int */;
  treesPlanted: number /* int */;
  co2Captured: number /* float64 */;
}
export interface GetUserStatsResponse {
  listeningTime: number /* int */;
  wordsKnown: number /* int */;
  dayStreak: number /* int */;
  waterDropletsCount: number /* int */;
  waterDropletsNeeded: number /* int */;
  treesPlanted: number /* int */;
  chaptersCompleted: number /* int */;
  co2Captured: number /* float64 */;
  level: number /* int */;
  timeToNextLevel: number /* int */;
  completedLessonsCount: number /* int */;
  reviewedCardsCount: number /* int */;
}
/**
 * GetPublicUserStatsResponse is the response for public user stats (viral loop)
 */
export interface GetPublicUserStatsResponse {
  name: string;
  wordsKnown: number /* int */;
  dayStreak: number /* int */;
  listeningTime: number /* int */; // in seconds
  daysSinceJoin: number /* int */; // days since user joined
}
export interface AdminUserStatResult {
  userId: number /* int64 */;
  name: string;
  email: string;
  lastStreakAt: string;
  waterDroplets: number /* int */;
  streak: number /* int */;
  role: 'admin' | 'free' | 'premium' | 'trial' | 'temp' | 'partner' | 'temp_trial' | 'temp_premium' | 'comp';
  isLifeTimePremium: boolean;
  coursesCompleted: number /* int */;
  chaptersCompleted: number /* int */;
  knownWords: number /* int */;
  learningWords: number /* int */;
  listenTime: number /* int */;
  createdAt: string;
  totalCardRepititions: number /* int */;
  hasPushToken: boolean;
  trialEndsAt: string;
  lastDevice?: AdminUserLastDevice;
  /**
   * ActivationStep mirrors user_settings.activation_step. Powers the
   * "Unlock Feed" cell in the admin user table — step > 4 means the feed
   * is already unlocked, so the row hides the button.
   */
  activationStep: number /* int */;
}
export interface AdminUserLastDevice {
  platform: string;
  modelName: string;
  lastSeenAt: string;
}
export interface GetStatsTableRequest {
  tableType: string;
}
export interface StatsData {
  date: string;
  value: number /* int */;
}
export interface GetStatsTableResponse {
  tableName: string;
  data: StatsData[];
}
export interface PostListenTimeRequest {
  listenTime: number /* int */;
  localDate: string; // Client's local date in YYYY-MM-DD format (e.g., "2025-10-02")
  /**
   * Optional: Only sent from feed view to track lesson interactions
   */
  trackLessonInteraction?: LessonInteractionInfo;
  lessonId?: number /* int64 */; // Only needed when tracking lesson interaction
  completed?: boolean; // Set to true when user reaches ~90% of video
}
export interface PostIncrementStreakResponse {
  dayStreak: number /* int */;
  message: string;
}
export interface GetWaterDropletsResponse {
  waterDropletsCount: number /* int */;
}
export interface GetAdminStatsResponse {
  userStats: AdminUserStatResult[];
  adminStats: AdminStatistics;
  nextPage: number /* int */;
  totalCount: number /* int */;
}
export interface AdminStatistics {
  freeUserCount: number /* int */;
  premiumUserCount: number /* int */;
  trialUserCount: number /* int */;
  partnerUserCount: number /* int */;
  totalVideoDurationPublished: string;
  totalVideoDurationUnpublished: string;
  videoLevelDurations: VideoLevelDurations;
}
export interface VideoLevelDurations {
  noLevel: string;
  a0: string;
  lowerA1: string;
  upperA1: string;
  lowerA2: string;
  upperA2: string;
  lowerB1: string;
  upperB1: string;
  lowerB2: string;
  upperB2: string;
  lowerC1: string;
  upperC1: string;
  c2: string;
}
/**
 * GetDailyListeningResponse response for getting daily listening time
 */
export interface GetDailyListeningResponse {
  dailyListeningSeconds: number /* int */;
  canClaimStreakToday: boolean; // Whether user can claim their streak today (hasn't claimed in last 12 hours)
  message: string;
}
export interface GetDailyListeningRangeRequest {
  startDate: string;
  endDate: string;
}
export interface GetDailyListeningRangeResponse {
  data: StatsData[];
}
export interface GetStreakDaysRequest {
  startDate: string;
  endDate: string;
}
export interface GetStreakDaysResponse {
  data: StatsData[];
}
/**
 * AdminCreateUserRequest is the request to create a new user by an admin
 */
export interface AdminCreateUserRequest {
  email: string;
  password: string;
  displayName: string;
  role: 'admin' | 'free' | 'premium' | 'trial' | 'temp' | 'partner' | 'temp_trial' | 'temp_premium' | 'comp';
}
/**
 * AdminCreateUserResponse is the response after creating a new user
 */
export interface AdminCreateUserResponse {
  userId: number /* int64 */;
  message: string;
}
export interface UserDeletionLogEntry {
  id: number /* int64 */;
  userId: number /* int64 */;
  email: string;
  displayName: string;
  role: string;
  deletedAt: string;
  reason: string;
}
export interface GetUserDeletionLogResponse {
  entries: UserDeletionLogEntry[];
  totalCount: number /* int */;
  page: number /* int */;
}

//////////
// source: stripe_dto.go

export interface StripeCheckoutResponse {
  clientSecret: string;
  id: string;
}
export interface StripeSessionStatusResponse {
  sessionStatus: string;
  paymentStatus: string;
}

//////////
// source: sync_dto.go

export interface SyncDefinition {
  targetWord: string;
  nativeWord: string;
  targetLanguage: string;
  nativeLanguage: string;
}
export interface SyncWord {
  wordKey: string;
  status: string;
  updatedAt: any /* time.Time */;
  definitions: SyncDefinition[];
}
export interface GetSyncResponse {
  words: SyncWord[];
  serverTimestamp: any /* time.Time */;
  isFullDump: boolean;
}
export interface SyncChangeDefinition {
  targetWord: string;
  nativeWord: string;
  targetLanguage: string;
  nativeLanguage: string;
  hidden?: boolean;
}
export interface SyncChangeWord {
  wordKey: string;
  status: string;
  updatedAt: any /* time.Time */;
  definitions: SyncChangeDefinition[];
}
export interface PostSyncRequest {
  changes: SyncChangeWord[];
}
export interface SyncResult {
  wordKey: string;
  accepted: boolean;
  serverStatus?: string;
  serverUpdatedAt?: any /* time.Time */;
}
export interface PostSyncResponse {
  results: SyncResult[];
  serverTimestamp: any /* time.Time */;
}

//////////
// source: tutorial_dto.go

/**
 * TutorialItemID is exported for TypeScript type generation
 * tygo:name TutorialItemID
 */
export type TutorialItemID = 'placement_test' | 'click_word_demo' | 'highlight_phrase_demo' | 'save_to_known_demo' | 'complete_activity' | 'complete_lesson' | 'skip_activity_demo' | 'complete_chapter' | 'review_dictionary' | 'review_cards' | 'parrot_method' | 'customize_experience' | 'share_with_friend';
/**
 * GetTutorialProgressResponse is the response for GET /auth/tutorial/progress
 */
export interface GetTutorialProgressResponse {
  completedItems: TutorialItemID[];
  isDismissed: boolean;
}
/**
 * PostCompleteTutorialItemRequest is the request for POST /auth/tutorial/complete
 */
export interface PostCompleteTutorialItemRequest {
  itemId: TutorialItemID;
}
/**
 * PostCompleteTutorialItemResponse is the response for POST /auth/tutorial/complete
 */
export interface PostCompleteTutorialItemResponse {
  message: string;
}
/**
 * PostDismissTutorialResponse is the response for POST /auth/tutorial/dismiss
 */
export interface PostDismissTutorialResponse {
  message: string;
}
/**
 * GetDemoWordResponse is the response for GET /auth/tutorial/demoword
 * Returns a demo video word that can be used to teach word clicking
 */
export interface GetDemoWordResponse {
  demoWord: UserVideoWord;
}
/**
 * GetDemoPhraseResponse is the response for GET /auth/tutorial/demophrase
 * Returns demo video words for phrase highlighting tutorial
 */
export interface GetDemoPhraseResponse {
  demoWords: UserVideoWord[];
  phraseTranslation: string;
  phraseId: number /* int64 */;
}

//////////
// source: user_chapter_like_dto.go

/**
 * PutChapterLikeRequest is the request body for setting a chapter like status
 */
export interface PutChapterLikeRequest {
  chapterId: number /* int64 */;
  isLiked: boolean;
}
/**
 * GetChapterLikeRequest is the query params for getting a chapter like status
 */
export interface GetChapterLikeRequest {
  ChapterID: number /* int64 */;
}
/**
 * ChapterLikeResponse is the response for chapter like operations
 */
export interface ChapterLikeResponse {
  chapterId: number /* int64 */;
  isLiked: boolean;
  message: string;
}
/**
 * LikedChapterItem represents a chapter the user has liked
 */
export interface LikedChapterItem {
  chapterId: number /* int64 */;
  title: string;
  youtubeId: string;
  aspectRatio: string;
  lessonIds: number /* int64 */[];
  likedAt: string;
}
/**
 * GetLikedChaptersResponse returns all chapters liked by the user
 */
export interface GetLikedChaptersResponse {
  chapters: LikedChapterItem[];
  nextPage: number /* int */;
  totalCount: number /* int */;
}

//////////
// source: user_device_dto.go

/**
 * Timestamps are serialized as RFC3339 strings so tygo emits a `string` field
 * to TypeScript rather than `any /* time.Time *\/`.
 */
export interface UserDeviceDTO {
  id: number /* int64 */;
  userId: number /* int64 */;
  deviceFingerprint: string;
  platform: string;
  clientVersion: string;
  otaUpdateId: string;
  deviceModel: string;
  deviceModelName: string;
  osVersion: string;
  firstSeenAt: string;
  lastSeenAt: string;
  sightingCount: number /* int64 */;
}
export interface GetUserDevicesResponse {
  devices: UserDeviceDTO[];
}

//////////
// source: user_dto.go

export interface GetUserInfoResponse {
  id: number /* int64 */;
  firstName: string;
  joinDate: string;
  email: string;
  role: UserRole;
  isTemp: boolean;
  isPremium: boolean;
  isTrial: boolean;
  isFree: boolean;
  isTempTrial: boolean;
  isTempPremium: boolean;
  isComp: boolean;
  avatarUrl: string;
  hasPushToken: boolean;
  stripeCustomerId: string;
  isFreeTrialUsed: boolean;
  placementResult: CEFRLevel;
  parrotLevel: number /* int */;
  authID: string;
  revenueCatId: string; // UUID for RevenueCat app_user_id
}
export interface PutNameRequest {
  name: string;
}
export interface PostIOSPushTokenRequest {
  token: string;
}
export interface PostExpoPushTokenRequest {
  token: string;
}
export type UserRole = 'admin' | 'free' | 'premium' | 'trial' | 'temp' | 'partner' | 'temp_trial' | 'temp_premium' | 'comp';
export interface PutUserRoleRequest {
  userID: number /* int64 */;
  role: UserRole;
}
export interface DevChangeRoleRequest {
  role: UserRole;
}
/**
 * DevDemoteRoleResponse is returned by the preview/staging-only test endpoint
 * that resets the authenticated user to the free role. It echoes the role the
 * user held before the demotion so Revyl/test diagnostics can confirm setup.
 */
export interface DevDemoteRoleResponse {
  message: string;
  previousRole: UserRole;
  role: UserRole;
}
export interface PostOnboardingRequest {
  onboarding: Onboarding;
}
export interface Onboarding {
  id: number /* int64 */;
  userId: number /* int64 */;
  whyDoYouWantToLearn: 'travel' | 'work' | 'friends_and_family' | 'school' | 'self_improvement' | 'other' | 'no_value' | 'romance';
  whenDoYouWantToBeFluentBy: number;
  howMuchCanYouStudyEachDay: number;
  whatIsYourBiggestChallengeWithLearningLanguage: 'no_time' | 'where_to_start' | 'staying_motivated' | 'grammar_issues' | 'speaking_practice' | 'listening_practice' | 'vocabulary_overload' | 'no_value';
  currentSpanishLevel: 'im_new_to_the_language' | 'i_know_some_common_words' | 'i_can_have_basic_conversations' | 'i_can_talk_about_various_topics' | 'i_can_discuss_most_topics_in_detail' | 'no_value';
  preferredTopic: 'general' | 'travel' | 'food' | 'small_talk' | 'vlogs_grwms' | 'sports' | 'grammar' | 'romance' | 'no_value';
  howDidYouHearAboutUs: any /* domain.ReferralSource */;
  createdAt: any /* time.Time */;
}
export type LearningReason = 'travel' | 'work' | 'friends_and_family' | 'school' | 'self_improvement' | 'other' | 'no_value' | 'romance';
export type LanguageLearningChallenge = 'no_time' | 'where_to_start' | 'staying_motivated' | 'grammar_issues' | 'speaking_practice' | 'listening_practice' | 'vocabulary_overload' | 'no_value';
export type LanguageLevelOnboarding = 'im_new_to_the_language' | 'i_know_some_common_words' | 'i_can_have_basic_conversations' | 'i_can_talk_about_various_topics' | 'i_can_discuss_most_topics_in_detail' | 'no_value';
export type PreferredTopic = 'general' | 'travel' | 'food' | 'small_talk' | 'vlogs_grwms' | 'sports' | 'grammar' | 'romance' | 'no_value';
export type ReferralSource = any /* domain.ReferralSource */;
export interface UserSettings {
  userId: number /* int64 */;
  isSoundEffectsEnabled: boolean;
  isWordListEnabled: boolean;
  isGuessTheWordEnabled: boolean;
  isFillInTheBlankEnabled: boolean;
  isMatchTheWordsEnabled: boolean;
  isSpellTheWordEnabled: boolean;
  isRepeatAfterMeEnabled: boolean;
  isBuildTheSentenceEnabled: boolean;
  showWordTutorialNextLesson: boolean;
  showFeedTutorialNextLesson: boolean;
  userFeedLevel: CEFRLevel;
  userFeedDialect: 'latam' | 'spain' | 'unknown';
  areFeedActivitiesEnabled: boolean;
  activityCount: number /* int */;
  isTutorialDismissed: boolean; // Whether feed tutorial has been dismissed
  userFeedTags: string[]; // Selected topic tags for feed personalization
  hasCompletedFeedTagsOnboarding: boolean; // True after user completes "Tune Your Feed" onboarding
  voiceAccent: 'mexican' | 'spain' | 'argentine' | 'colombian' | 'neutral'; // Spanish accent for TTS pronunciation
  voiceGender: 'female' | 'male'; // Voice gender for TTS
  isFlightSchoolEnabled: boolean; // Whether Flight School is enabled (for A1+ users)
  dailyListeningGoalMinutes: number /* int */; // Daily listening goal in minutes
  wordClickTutorialComplete: boolean;
  phraseHighlightTutorialComplete: boolean;
  activationStep: number /* int */;
  highestEarnedLevel: number /* int */;
}
/**
 * Request and response types
 */
export interface PutUserSettingsRequest {
  settings: UserSettings;
}
export interface GetUserSettingsResponse {
  settings: UserSettings;
}
export interface PostPlacementResultRequest {
  placementResult: CEFRLevel;
}
export interface SyncRcStatusRequest {
  rcAppUserID: string;
  purchaseCompleted: boolean;
  selectedPlan: string;
  packageId: string;
}
export interface SyncRcStatusResponse {
  message: string;
  role: UserRole;
  subscriptionStatus: string;
  checkedAppUserIDs: string[];
  activationSource: string;
  isDefinitive: boolean;
}
export interface PostEmailInUseRequest {
  email: string;
}
export interface PostEmailInUseResponse {
  emailInUse: boolean;
}
export interface CreateImpersonationTokenResponse {
  userId: number /* int64 */;
  email: string;
  role: UserRole;
  token: string;
  expiresInSeconds: number /* int64 */;
}
/**
 * SignupV2Request is the request for the v2 signup endpoint
 * This endpoint is called after PocketBase account creation with auth already established
 */
export interface OnboardingWord {
  word: string;
  translation: string;
}
export interface SignupV2Request {
  rcAnonymousId: string;
  onboarding: Onboarding;
  expoPushToken?: string;
  onboardingWords?: OnboardingWord[];
}
/**
 * SignupV2Response is the response from v2 signup
 */
export interface SignupV2Response {
  userId: number /* int64 */;
  role: UserRole;
  revenueCatId: string; // UUID for RevenueCat app_user_id - use this for Purchases.logIn()
}
export interface WebSignupV2Request {
  checkoutSessionId: string;
  sku: string;
  email?: string;
  password?: string;
  displayName?: string;
  tempAuthToken?: string;
  onboarding: Onboarding;
  onboardingWords?: OnboardingWord[];
}
export interface WebSignupV2Response {
  userId: number /* int64 */;
  role: UserRole;
  stripeCustomerId: string;
  authToken?: string;
}
/**
 * TransferSubscriptionRequest is the request to transfer a subscription for existing users
 */
export interface TransferSubscriptionRequest {
  rcAnonymousId: string;
}
/**
 * TransferSubscriptionResponse is the response from subscription transfer
 */
export interface TransferSubscriptionResponse {
  success: boolean;
  userId: number /* int64 */;
  role: UserRole;
  revenueCatId: string; // UUID for RevenueCat app_user_id - use this for Purchases.logIn()
}
/**
 * PutAdminUserFieldsRequest is the request for admin to update user fields
 * All fields are optional - only non-nil fields will be updated
 */
export interface PutAdminUserFieldsRequest {
  userID: number /* int64 */;
  name?: string; // Optional - display name
  streak?: number /* int */; // Optional - streak count
  email?: string; // Optional - email address
  activationStep?: number /* int */; // Optional - advance the activation journey (monotonic; can only go up)
}

//////////
// source: user_path_dto.go

/**
 * DTOs
 */
export interface GetAllChaptersRequest {
  courseId: number /* int64 */;
}
export interface GetCourseInfoResponse {
  course: Course;
  chapters: Chapter[];
}
export interface Course {
  courseId: number /* int64 */;
  title: string;
  description: string;
  published: boolean;
  topic: CourseTopic;
  level: CourseLevel;
  isSystemCourse: boolean;
}
export interface CourseWithProgress {
  course: Course;
  totalChapters: number /* int64 */;
  completedChapters: number /* int64 */;
  progress: number /* float64 */;
}
export interface CourseTopic {
  id: number /* int64 */;
  name: string;
  emoji: string;
  displayOrder: number /* int */;
}
export interface CourseLevel {
  id: number /* int64 */;
  name: string;
  difficulty: number /* int */;
}
export interface CourseTree {
  topic: CourseTopic;
  courses: Course[];
}
export interface Chapter {
  chapterId: number /* int64 */;
  youtubeId?: string;
  title: string;
  description: string;
  status: string;
  published: boolean;
  aspectRatio: '16:9' | '9:16';
  isFullVidHidden: boolean;
  videoLevel: number /* float64 */;
  videoLevelText: 'No Level' | 'A0' | 'A0 - Scratch' | 'Lower A1' | 'Upper A1' | 'Lower A2' | 'Upper A2' | 'Lower B1' | 'Upper B1' | 'Lower B2' | 'Upper B2' | 'Lower C1' | 'Upper C1' | 'C2';
  topics: { id: number /* int64 */; Name: string; Confidence: number /* float64 */; }[];
  duration: any /* domain.HHMMSS */;
  qaStatus: QAStatus;
  isManualSetLevel: boolean;
  dialect: 'latam' | 'spain' | 'unknown';
  cloudflareId?: string;
  player: 'unset' | 'cloudflare' | 'youtube';
  effectivePlayer: 'unset' | 'cloudflare' | 'youtube';
  creatorIsLicensed: boolean;
  hasCreator: boolean;
  assignedTo: string;
  likeCount: number /* int */;
  videoDisplayMode: any /* domain.VideoDisplayMode */;
  ytViews?: number /* int */;
  ytLikes?: number /* int */;
  ytSubscribers?: number /* int */;
  isEmbeddable?: boolean;
}
export type QAStatus = '' | 'words' | 'activities' | 'done';
export interface GetAllLessonRequest {
  chapterId: number /* int64 */;
}
export interface GetAllLessonsResponse {
  chapter: Chapter;
  lessons: Lesson[];
}
export interface GetCompletedLessonsRequest {
  userId: number /* int64 */;
}
export interface GetCompletedLessonsResponse {
  lessons: UserLessonWithCourse[];
}
export interface PostChapterCompletedRequest {
  chapterId: number /* int64 */;
}
export interface PostChapterCompletedResponse {
  message: string;
  waterDropletsAwarded: number /* int */;
}
export interface PostChapterSkippedRequest {
  chapterId: number /* int64 */;
}
export interface PostChapterSkippedResponse {
  message: string;
}
export interface PostCourseCompletedRequest {
  courseId: number /* int64 */;
}
export interface PostCourseCompletedResponse {
  message: string;
  waterDropletsAwarded: number /* int */;
}
export interface GetAllCoursesResponse {
  courses: Course[];
}
export interface GetAllCoursesProgressResponse {
  courses: CourseWithProgress[];
}
export interface GetAllCourseTreesResponse {
  courseTrees: CourseTree[];
}
export interface GetLessonDataRequest {
  lessonId: number /* int64 */;
}
export type ProgressStatus = 'completed' | 'current' | 'locked' | 'skipped';
/**
 * DTOs
 */
export interface Lesson {
  lessonId: number /* int64 */;
  title: string;
  description: string;
  status: ProgressStatus;
  sequence: number /* int */;
  start: number;
  end: number;
  isEdited: boolean;
}
export interface UserLesson {
  userId: number /* int64 */;
  lessonId: number /* int64 */;
  completedAt: any /* time.Time */;
}
export interface UserLessonWithCourse {
  userLesson: UserLesson;
  courseId: number /* int64 */;
}
export type AspectRatio = '16:9' | '9:16';
export interface VideoActivity {
  youtubeId: string;
  start: number /* int */;
  end: number /* int */;
  aspectRatio: AspectRatio;
  userVideoWords: UserVideoWord[];
  cloudflareId?: string;
  player: 'unset' | 'cloudflare' | 'youtube';
}
export type UserWordStatus = 'new' | 'learning' | 'known' | 'deleted';
export interface UserVideoWord {
  videoWordId: number /* int64 */;
  start: number /* int */;
  /**
   * OPTIONAL VALUE if its not set it will have a value of 0
   */
  duration: number /* int */;
  status: UserWordStatus;
  wordKey: string;
  displayText: string;
  clickHint: string; // deprecated: always empty, kept for backwards compatibility
  definition: Definition;
}
export interface Definition {
  definitionId: number /* int64 */;
  targetWord: string;
  nativeWord: string;
  wordKey: string;
}
/**
 * These types are EXPORT VERSION sensitive, careful changeing
 */
export interface FillActivity {
  start: number /* int */;
  end: number /* int */;
  youtubeId: string;
  aspectRatio: AspectRatio;
  fillInTheBlank: FillInTheBlank;
  approved: boolean;
  cloudflareId?: string;
  player: 'unset' | 'cloudflare' | 'youtube';
}
export interface FillInTheBlank {
  question: string;
  options: Option[];
}
export interface Option {
  choice: string;
  correct: boolean;
}
export interface GuessActivity {
  question: string;
  options: Option[];
  approved: boolean;
}
/**
 * These types are EXPORT VERSION sensitive, careful changeing
 */
export interface MatchActivity {
  matchData: MatchData[];
  approved: boolean;
}
export interface MatchData {
  spanish: string;
  english: string;
}
/**
 * These types are EXPORT VERSION sensitive, careful changeing
 */
export interface WriteActivity {
  question: string;
  answer: string;
  approved: boolean;
}
export interface SpeakActivity {
  youtubeId: string;
  start: number;
  end: number;
  aspectRatio: AspectRatio;
  userVideoWords: UserVideoWord[];
  approved: boolean;
  cloudflareId?: string;
  player: 'unset' | 'cloudflare' | 'youtube';
}
export type SentenceActivity = SpeakActivity;
export interface PostLessonCompletedRequest {
  lessonId: number /* int64 */;
}
export interface PostLessonCompletedResponse {
  message: string;
  waterDropletsAwarded: number /* int */;
}
export interface PostActivityCompleteRequest {
  lessonId: number /* int64 */;
  activityType: string;
}
export interface PostActivityCompleteResponse {
  message: string;
  waterDropletsAwarded: number /* int */;
}
export interface PutLessonCompletedRequest {
  lessonId: number /* int64 */;
}
export interface PostActivityCompletedRequest {
  lessonId: number /* int64 */;
  activityType: 'fill' | 'guess' | 'match' | 'write' | 'video' | 'full_video';
}
export interface GetLessonDataResponse {
  lesson: Lesson;
  video: VideoActivity;
  fill: FillActivity;
  guess: GuessActivity;
  match: MatchActivity;
  write: WriteActivity;
  speak: SpeakActivity;
  sentence: SentenceActivity;
  isLastChapter: boolean;
  isLastLesson: boolean;
  nextLessonId: number /* int64 */;
  /**
   * course id the lesson belongs to
   */
  courseId: number /* int64 */;
  videoDisplayMode: any /* domain.VideoDisplayMode */;
}

//////////
// source: user_path_dto_v2.go

export interface GetLessonDataResponseV2 {
  lesson: Lesson;
  video: VideoActivity;
  sentence: SentenceActivity; // Only sentence activity (dynamically generated)
  isLastChapter: boolean;
  isLastLesson: boolean;
  nextLessonId: number /* int64 */;
  courseId: number /* int64 */;
  videoDisplayMode: any /* domain.VideoDisplayMode */;
}

//////////
// source: word_err_dto.go

export interface PostWordErrorRequest {
  videoWordId: number /* int64 */;
  feedback: string;
  lessonId: number /* int64 */;
}
export interface PutResolveWordErrorRequest {
  wordErrorId: number /* int64 */;
  isResolved: boolean;
}
export interface GetWordErrorsResponse {
  wordErrors: WordError[];
}
export interface WordError {
  id: number /* int64 */;
  userId: number /* int64 */;
  email: string;
  displayName: string;
  videoWordId: number /* int64 */;
  lessonId: number /* int64 */;
  chapterId: number /* int64 */;
  displayWord: string;
  targetWord: string;
  nativeWord: string;
  clickHint: string; // deprecated: always empty, kept for backwards compatibility
  /**
   * one word is wrapped in <word>...</word> tag
   */
  context: string;
  timestamp: string;
  isResolved: boolean;
  feedback: string;
}
